import { LOGGING_ENABLED } from '../config.js';

export const CHAT_ERROR_REASON = {
  // Auth attempt exceeded AUTH_TIMEOUT (typically a slow connection).
  CONNECTION_TIMEOUT: 'CONNECTION_TIMEOUT',
  // WebSocket/transport failed to connect.
  CONNECTION_FAILED: 'CONNECTION_FAILED',
  // Server reached but login was rejected (bad/expired token, no access).
  AUTH_REJECTED: 'AUTH_REJECTED',
  // An established session's credentials were rejected; host should re-auth.
  SESSION_EXPIRED: 'SESSION_EXPIRED',
};

/**
 * Payload posted to the Flutter host over FlutterBridge.
 *
 * @typedef {'fatal_error' | 'auth_result' | 'session_expired'} NativeHostMessageType
 * @typedef {'CONNECTION_TIMEOUT' | 'CONNECTION_FAILED' | 'AUTH_REJECTED' | 'SESSION_EXPIRED'
 *   | 'AUTHENTICATED' | 'ALREADY_AUTHENTICATED' | 'INVALID_TOKEN' | 'NO_RESPONSE' | 'FAILED'} NativeHostStatus
 * @typedef {{
 *   type: NativeHostMessageType,
 *   ok?: boolean,
 *   reason: NativeHostStatus,
 * }} NativeHostPayload
 */

// Sends a JSON payload to the native host over the Flutter JavaScript channel.
// Returns false when the channel was unavailable or the send threw, so callers
// that must not silently drop a message can react.
/**
 * @param {NativeHostPayload} payload
 * @returns {boolean}
 */
export function postToNativeHost(payload) {
  const message = JSON.stringify(payload);
  try {
    if (window.FlutterBridge && typeof window.FlutterBridge.postMessage === 'function') {
      if (LOGGING_ENABLED) {
        console.log(`postToNativeHost ${message}`);
      }
      window.FlutterBridge.postMessage(message);
      return true;
    }
    // Without the channel the host never learns the outcome, so the user is
    // left looking at the blank fallback view indefinitely.
    if (LOGGING_ENABLED) {
      console.warn(`postToNativeHost: FlutterBridge unavailable, dropping ${message}`);
    }
  } catch (err) {
    console.error('Failed to post message to native host', message, err);
  }
  return false;
}

/**
 * @param {'CONNECTION_TIMEOUT' | 'CONNECTION_FAILED' | 'AUTH_REJECTED'} reason
 */
export function notifyChatFatalError(reason) {
  postToNativeHost({ type: 'fatal_error', ok: false, reason });
}

// An established session was rejected and cannot be recovered from cached
// credentials. Distinct from fatal_error because it is actionable: the host is
// expected to obtain a fresh JWT and call AngkasAuth.login() with it.
export function notifySessionExpired() {
  postToNativeHost({ type: 'session_expired', ok: false, reason: CHAT_ERROR_REASON.SESSION_EXPIRED });
}
